import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDataChannel } from "@livekit/components-react";
import { plyxApi } from "@/lib/plyxApi";
import { Send, MessageSquare, X, VolumeX, Trash2 } from "lucide-react";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { toast } from "sonner";

/**
 * LiveChatPanel — TikTok-style live chat for Plyx Live.
 *
 * - Realtime delivery via LiveKit data channel on topic "plyx-chat".
 * - Backend persists every message so late-joiners get the recent scrollback
 *   via GET /plyx/live/{room}/chat.
 * - POST /plyx/live/{room}/chat is the durable write path. Sending also
 *   broadcasts over the data channel for instant echo on every screen.
 * - 140-char client limit + 60-msg/min server throttle.
 */
const LiveChatPanel = ({ roomName, open, onClose, isMod = false }) => {
  const { user } = usePlyxAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  const { send: sendData, message: dataMessage } = useDataChannel("plyx-chat");

  // Load scrollback on first open.
  useEffect(() => {
    if (!open || !roomName) return;
    let alive = true;
    plyxApi.get(`/plyx/live/${roomName}/chat`).then((r) => {
      if (!alive) return;
      setMessages(r.data?.messages || []);
    }).catch(() => {});
    return () => { alive = false; };
  }, [open, roomName]);

  // Receive realtime messages.
  useEffect(() => {
    if (!dataMessage) return;
    try {
      const txt = new TextDecoder().decode(dataMessage.payload);
      const msg = JSON.parse(txt);
      if (msg.type === "chat" && msg.text) {
        setMessages((prev) => {
          if (msg.chat_id && prev.some((m) => m.chat_id === msg.chat_id)) return prev;
          return [...prev, {
            chat_id: msg.chat_id,
            sender_handle: msg.handle,
            sender_avatar_url: msg.avatar,
            text: msg.text,
            created_at: msg.ts,
          }].slice(-200);
        });
      } else if (msg.type === "chat_deleted" && msg.chat_id) {
        // Remove the bubble for everyone connected.
        setMessages((prev) => prev.filter((m) => m.chat_id !== msg.chat_id));
      }
    } catch {/* ignore */}
  }, [dataMessage]);

  // Auto-scroll to bottom on new message.
  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = useCallback(async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      const r = await plyxApi.post(`/plyx/live/${roomName}/chat`, { text: t });
      const msg = {
        chat_id: r.data.chat_id,
        sender_handle: user?.handle,
        sender_avatar_url: user?.avatar_url,
        text: r.data.text,
        created_at: r.data.created_at,
      };
      setMessages((prev) => [...prev, msg].slice(-200));
      // Broadcast to other viewers + the broadcaster.
      try {
        const payload = new TextEncoder().encode(JSON.stringify({
          type: "chat",
          chat_id: msg.chat_id,
          text: msg.text,
          handle: msg.sender_handle,
          avatar: msg.sender_avatar_url,
          ts: msg.created_at,
        }));
        sendData?.(payload, { topic: "plyx-chat", reliable: true });
      } catch {/* ignore */}
      setText("");
    } catch (e) {
      const detail = e.response?.data?.detail || "Could not send";
      toast.error(detail);
    } finally {
      setSending(false);
    }
  }, [text, roomName, sendData, sending, user]);

  // Soft-delete a single chat message (own message OR broadcaster). Removes
  // locally + broadcasts a chat_deleted event so every connected client
  // pops the bubble at the same instant.
  const deleteMsg = useCallback(async (chatId) => {
    try {
      await plyxApi.delete(`/plyx/live/${roomName}/chat/${chatId}`);
      setMessages((prev) => prev.filter((m) => m.chat_id !== chatId));
      try {
        const payload = new TextEncoder().encode(JSON.stringify({
          type: "chat_deleted",
          chat_id: chatId,
        }));
        sendData?.(payload, { topic: "plyx-chat", reliable: true });
      } catch {/* ignore */}
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  }, [roomName, sendData]);

  const muteUser = useCallback(async (handle) => {
    try {
      const r = await plyxApi.post(`/plyx/live/${roomName}/mute`, { handle, minutes: 5 });
      toast.success(`@${handle} muted for 5 minutes`);
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not mute");
    }
  }, [roomName]);

  if (!open) return null;

  return (
    <div data-testid="plyx-live-chat-panel" className="absolute top-0 right-0 bottom-0 w-full sm:w-96 bg-black/85 backdrop-blur border-l border-white/10 flex flex-col z-30 pointer-events-auto">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-white/85" />
          <p className="font-bold text-sm text-white">Live chat</p>
        </div>
        <button
          data-testid="plyx-live-chat-close"
          onClick={onClose}
          className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
          aria-label="Close chat"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto px-3 py-2 space-y-1.5">
        {messages.length === 0 && (
          <p className="text-white/40 text-xs text-center mt-6">Say hi — first messages always go off 🔥</p>
        )}
        {messages.map((m, i) => {
          const isOwn = m.sender_handle && user?.handle && m.sender_handle === user.handle;
          const canDelete = isOwn || isMod;
          const canMute = isMod && !isOwn;
          return (
            <div key={m.chat_id || i} className="group text-white text-sm break-words flex items-start gap-1.5">
              <div className="flex-1 min-w-0">
                <span className="font-bold text-[#FFCC2E]">@{m.sender_handle}</span>
                <span className="ml-2 text-white/90">{m.text}</span>
              </div>
              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5 shrink-0">
                {canMute && (
                  <button
                    type="button"
                    onClick={() => muteUser(m.sender_handle)}
                    title={`Mute @${m.sender_handle} for 5 minutes`}
                    aria-label={`Mute ${m.sender_handle}`}
                    data-testid={`plyx-live-chat-mute-${m.sender_handle}`}
                    className="w-6 h-6 rounded-full bg-white/5 hover:bg-orange-600/40 text-orange-300 hover:text-orange-100 flex items-center justify-center"
                  >
                    <VolumeX className="w-3 h-3" />
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => m.chat_id && deleteMsg(m.chat_id)}
                    title="Delete message"
                    aria-label="Delete message"
                    data-testid={`plyx-live-chat-delete-${m.chat_id || i}`}
                    className="w-6 h-6 rounded-full bg-white/5 hover:bg-red-600/40 text-red-300 hover:text-red-100 flex items-center justify-center"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-white/10 p-2 flex gap-2 items-center">
        <input
          data-testid="plyx-live-chat-input"
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 140))}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          placeholder="Say something…"
          maxLength={140}
          className="flex-1 min-w-0 rounded-full bg-white/5 border border-white/10 px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#00E0C7]"
        />
        <button
          data-testid="plyx-live-chat-send"
          onClick={send}
          disabled={!text.trim() || sending}
          className="w-9 h-9 shrink-0 rounded-full bg-gradient-to-r from-[#00E0C7] to-[#FFCC2E] text-black flex items-center justify-center disabled:opacity-40"
          aria-label="Send chat"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
      <p className="text-[10px] text-white/30 text-center pb-2">{text.length}/140 · be kind</p>
    </div>
  );
};

export default LiveChatPanel;
